import { useAuth } from '../../context/AuthContext';
import { useScreenSharePresenter } from '../../utils/screenShare';

/**
 * Student-side host for the tutor screen-share feature. Mounted globally so a
 * tutor's "watch my screen" request can prompt the student wherever they are
 * in the app — including mid-practice. Renders only fixed-position overlays,
 * so its position in the tree doesn't matter. Self-gates: returns null for
 * anyone who isn't a logged-in student.
 */
export default function ScreenShareHost() {
  const { student, token } = useAuth();
  const isStudent = student?.role === 'student';
  const studentId = isStudent ? student?.id ?? null : null;
  const { status, incomingRequest, accept, deny, stopSharing } = useScreenSharePresenter(
    studentId,
    token,
    isStudent,
  );

  if (!isStudent) return null;

  return (
    <>
      {incomingRequest && status === 'prompt' && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4"
          dir="rtl"
        >
          <div className="glass-card max-w-sm w-full p-6 space-y-4">
            <h3 className="font-extrabold text-lg">📺 בקשת צפייה במסך</h3>
            <p className="text-sm text-slate-300">
              <span className="font-bold">{incomingRequest.teacherName}</span> מבקש/ת
              לצפות במסך שלך בזמן אמת, כדי לעזור לך תוך כדי הלמידה.
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              תוכל/י לבחור איזה חלון או מסך לשתף, ולעצור את השיתוף בכל רגע. שום דבר
              לא מוקלט.
            </p>
            <div className="flex gap-3">
              <button
                onClick={accept}
                className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded-lg"
              >
                אישור ושיתוף
              </button>
              <button
                onClick={deny}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg"
              >
                לא עכשיו
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'connecting' && (
        <div
          className="fixed bottom-4 right-4 z-[120] glass-card px-4 py-2 text-sm text-slate-300"
          dir="rtl"
        >
          מתחבר…
        </div>
      )}

      {status === 'sharing' && (
        <div
          className="fixed bottom-4 right-4 z-[120] glass-card px-4 py-3 flex items-center gap-3"
          dir="rtl"
        >
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
          <span className="text-sm text-slate-200">המסך שלך משותף עם המורה</span>
          <button
            onClick={() => stopSharing(true)}
            className="bg-rose-600 hover:bg-rose-500 text-white text-xs px-2.5 py-1 rounded"
          >
            הפסק שיתוף
          </button>
        </div>
      )}
    </>
  );
}
